import type {
  StoryblokRichTextContext,
  StoryblokRichTextNode,
} from '@storyblok/richtext'
import { BlockTypes, richTextResolver } from '@storyblok/richtext'
import type { Sbrichtext } from '~/cms/storyblok/types/storyblok.d.ts'

// Reproduce the `textAlign -> style` conversion that the library's internal
// (non-exported) `processAttributes` performs. `context.render` serializes attrs
// verbatim, so spreading the raw heading attrs would emit an inert `textAlign`
// attribute and drop the alignment. Keeping the conversion here means a heading
// rendered as a span retains its style.
export const buildHeadingSpanAttrs = (
  attrs: Record<string, any> = {},
): Record<string, any> => {
  const { level, textAlign, style, ...rest } = attrs
  const styleStr = [style, textAlign && `text-align: ${textAlign};`]
    .filter(Boolean)
    .join(' ')

  return {
    ...rest,
    ...(styleStr ? { style: styleStr } : {}),
    class: `cms-heading cms-heading--h${level ?? 1}`,
  }
}

// Build the richtext resolver. When `ignoreHeadingKeepStyle` is set, headings are
// rendered as styled spans so they drop out of the document heading outline (SEO)
// while keeping the heading's look. Otherwise headings fall through to the
// library default, so callers that don't opt in are unaffected.
export const createRichtextResolver = (ignoreHeadingKeepStyle = false) =>
  richTextResolver({
    resolvers: {
      [BlockTypes.EMOJI]: (node) => {
        const emoji = (node.attrs as { emoji?: string } | undefined)?.emoji
        return `<span class="emoji">${emoji || ''}</span>`
      },
      ...(ignoreHeadingKeepStyle
        ? {
            [BlockTypes.HEADING]: (
              node: StoryblokRichTextNode<string>,
              { render }: StoryblokRichTextContext<string>,
            ) =>
              render('span', buildHeadingSpanAttrs(node.attrs), node.children),
          }
        : {}),
    },
  })

const defaultResolver = createRichtextResolver(false)

const useRichtext = (
  body: Sbrichtext | undefined,
  options: { ignoreHeadingKeepStyle?: boolean } = {},
) => {
  const { t } = useI18n()
  const { user } = useAgsUser()
  const currentShop = useCurrentShop()
  const locale = currentShop.value?.locale

  const resolver = options.ignoreHeadingKeepStyle
    ? createRichtextResolver(true)
    : defaultResolver

  return {
    content: computed(() => {
      if (!body) {
        return null
      }

      // Render HTML using the new resolver
      const html = resolver.render(body as any) as string

      const localeRegex =
        /href="(de|at|ch|fr-ch|fr-be|fr|be|nl|fi)\/([^'"\s>]+)/gi

      const strippedHtml = html?.replace(localeRegex, (_match, _lang, url) => {
        return `href="${url}"`
      })

      return (
        strippedHtml
          ?.replace(
            /href="\/?(?!http:|https:|mailto:|tel:|fax:)(\w+)/g,
            `href="/${locale}/$1`,
          )
          // solution for Goldner Club salutation and name
          ?.replace(
            /\[HONORIFIC\]/g,
            t(`global.salutation.${getSalutationKey(user?.value?.gender)}`) ||
              '',
          )
          ?.replace(/\[CUSTOMER\]/g, user?.value?.lastName || '')
      )
    }),
  }
}

export default useRichtext
